import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import * as productService from '../../services/productService';
import styles from './PublicCatalogPage.module.css';
import { resolveCategoryHeroUrl } from '../../utils/categoryHero';

const PublicCatalogPage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  // A failed load must be distinguishable from a genuinely empty catalogue: both leave
  // `products` at [], and without this flag the page renders "No treatments found" — telling
  // the visitor the catalogue is empty when the fetch actually failed. Card 53582.
  const [loadError, setLoadError] = useState(false);
  const [infoPopupProduct, setInfoPopupProduct] = useState(null);

  // Drag-to-scroll for product card containers
  useEffect(() => {
    const containers = document.querySelectorAll('.' + styles.productCards);
    const state = new Map();
    
    const onDown = (e) => {
      const el = e.currentTarget;
      state.set(el, { dragging: true, startX: e.pageX - el.offsetLeft, scrollL: el.scrollLeft });
      el.style.cursor = 'grabbing';
      el.style.scrollBehavior = 'auto';
      el.style.userSelect = 'none';
    };
    const onUp = (e) => {
      const el = e.currentTarget;
      state.delete(el);
      el.style.cursor = 'grab';
      el.style.scrollBehavior = 'smooth';
      el.style.userSelect = '';
    };
    const onMove = (e) => {
      const el = e.currentTarget;
      const s = state.get(el);
      if (!s || !s.dragging) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = s.scrollL - (x - s.startX) * 1.5;
    };
    
    containers.forEach(el => {
      el.addEventListener('mousedown', onDown);
      el.addEventListener('mouseup', onUp);
      el.addEventListener('mouseleave', onUp);
      el.addEventListener('mousemove', onMove);
    });
    
    return () => {
      containers.forEach(el => {
        el.removeEventListener('mousedown', onDown);
        el.removeEventListener('mouseup', onUp);
        el.removeEventListener('mouseleave', onUp);
        el.removeEventListener('mousemove', onMove);
      });
    };
  }, [categories, products]);

  // Hoisted out of the effect with useCallback so the error state's "Try again" re-runs this
  // very loader rather than a second copy of it that could drift from it. Card 53582.
  const loadData = useCallback(async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [catResponse, prodResponse] = await Promise.all([
        productService.getCategories(),
        productService.getProducts()
      ]);
      setCategories(catResponse.categories || []);
      setProducts(prodResponse.products || []);
    } catch (error) {
      console.error('Failed to load catalog:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tabs = [
    { id: 'all', label: 'All' },
    ...categories.map(cat => ({
      id: cat.slug,
      label: cat.name
    }))
  ];

  const categorySections = categories.map(cat => ({
    id: cat.slug,
    title: cat.name,
    subtitle: cat.shortDescription || cat.description || '',
    image: resolveCategoryHeroUrl(cat),
    products: products.filter(p => p.category?.slug === cat.slug)
  }));

  const filteredSections = activeTab === 'all' 
    ? categorySections 
    : categorySections.filter(s => s.id === activeTab);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Treatment Catalogue</h1>
        <p>Explore our full range of doctor-prescribed treatments and medications.</p>
      </div>

      {/* Category Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Category Sections */}
      <div className={styles.sections}>
        {filteredSections.map(section => (
          <div key={section.id} className={styles.section}>
            {/* Hero Card */}
            <div className={styles.heroCard}>
              <div
                className={styles.heroImage}
                style={{ backgroundImage: `url(${section.image})` }}
              />
              <div className={styles.heroContent}>
                <div className={styles.heroText}>
                  <h2>{section.title}</h2>
                  <p>{section.subtitle}</p>
                </div>
                <Link to="/register" className={styles.getStartedBtn}>
                  Get Started
                </Link>
              </div>
            </div>

            {/* Products */}
            <div className={styles.productsPanel}>
              <div className={styles.productsPanelHeader}>
                <Link to="/register" className={styles.viewAllLink}>
                  View All
                </Link>
              </div>
              
              <div className={styles.productCards}>
                {section.products.length > 0 ? (
                  section.products.map(product => (
                    <div key={product.id} className={`${styles.productCard} ${product.isComingSoon ? styles.comingSoon : product.isOutOfStock ? styles.outOfStock : ""}`}>
                      <div
                        className={styles.productImage}
                        style={{ backgroundImage: `url(${product.image || '/images/product-placeholder.webp'})` }}
                      >
                        {product.isComingSoon && (
                          <div className={styles.comingSoonOverlay}>Coming Soon</div>
                        )}
                        {product.isOutOfStock && (
                          <div className={styles.outOfStockOverlay}>Out of Stock</div>
                        )}
                      </div>
                      <div className={styles.productInfo}>
                        <span className={styles.productCategory}>
                          {product.category?.name || section.title.split(' ')[0]}
                        </span>
                        <h3 className={styles.productName}>{product.name}</h3>
                        <p className={styles.productDescription}>
                          {product.shortDescription || product.description?.substring(0, 100)}
                        </p>
                        {product.startingAtPrice > 0 ? (
                          <div className={styles.productPrice}>
                            Starting at ${Math.ceil(product.startingAtPrice / 100)}/mo
                          </div>
                        ) : null}
                        <div className={styles.productActions}>
                          <button
                            className={styles.infoBtn}
                            onClick={() => setInfoPopupProduct(product)}
                          >
                            Info
                          </button>
                          {product.isComingSoon ? (
                            <button className={`${styles.requestBtn} ${styles.requestBtnDisabled}`} disabled>
                              Coming Soon
                            </button>
                          ) : product.isOutOfStock ? (
                            <button className={`${styles.requestBtn} ${styles.requestBtnDisabled}`} disabled>
                              Out of Stock
                            </button>
                          ) : (
                            <Link to="/register" className={styles.requestBtn}>
                              Get Started
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.noProducts}>
                    <p>No products available in this category yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {loadError && (
        <div className={styles.empty} role="alert">
          <p>We couldn&apos;t load the treatment catalogue.</p>
          <button type="button" className={styles.requestBtn} onClick={loadData}>
            Try again
          </button>
        </div>
      )}

      {!loadError && filteredSections.length === 0 && (
        <div className={styles.empty}>
          <p>No treatments found for this category.</p>
        </div>
      )}

      {/* Product Info Popup Modal — no prices */}
      {infoPopupProduct && createPortal(
        <div className={styles.popupOverlay} onClick={() => setInfoPopupProduct(null)}>
          <div className={styles.popupModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.popupClose} onClick={() => setInfoPopupProduct(null)}>×</button>
            {(infoPopupProduct.lifestyleImageUrl || infoPopupProduct.primaryImage?.url || infoPopupProduct.image) && (
              <div className={styles.popupImageWrap}>
                <img
                  src={infoPopupProduct.lifestyleImageUrl || infoPopupProduct.primaryImage?.url || infoPopupProduct.image || '/images/product-placeholder.webp'}
                  alt={infoPopupProduct.name}
                  className={styles.popupImage}
                />
              </div>
            )}
            {infoPopupProduct.startingAtPrice > 0 && (
              <div className={styles.popupPricing}>
                <span className={styles.popupPricingLabel}>Starting at</span>
                <div className={styles.popupPriceRow}>
                  <span className={styles.popupOvalPrice}>
                    ${Math.ceil(infoPopupProduct.startingAtPrice / 100)}/mo
                  </span>
                </div>
              </div>
            )}
            <div className={styles.popupBody}>
              <span className={styles.popupCategory}>
                {infoPopupProduct.category?.name || ''}
              </span>
              <h2 className={styles.popupTitle}>{infoPopupProduct.name}</h2>
              <div
                className={styles.popupDescription}
                dangerouslySetInnerHTML={{
                  __html: (infoPopupProduct.detailedDescription || infoPopupProduct.shortDescription || infoPopupProduct.description || 'No additional information available for this product.').replace(/&nbsp;/gi, ' ')
                }}
              />
              <Link
                to="/register"
                className={styles.popupCta}
                onClick={() => setInfoPopupProduct(null)}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default PublicCatalogPage;
